// RelationContactPicker.tsx — Refined-Kontaktauswahl für den Verknüpfungs-Dialog.
// Zwei Modi: (1) Suchen (GET /contact?search, debounced) mit umrandetem Such-Feld
// + Ergebnisliste; (2) Neuen Kontakt anlegen (Person/Firma) via POST /contact
// (Kommunikation inline). Liefert das gewählte/angelegte Kontakt-Objekt via onSelect.

import * as React from "react";
import { geschlechtAusAnrede } from "../../utils/anredeGeschlecht";
import { apiUrl } from "../../utils/api";
import { buildPersonCommItems } from "../../utils/contactCommHelpers";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import { mitVerantwortungVon } from "../../utils/verantwortungErben";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";
import { VollanlageWeiterKnopf } from "../forms/VollanlageWeiter";
import type { ContactRef } from "../relationsInline/types";

const SearchIcon = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><circle cx="9" cy="9" r="6" /><path d="M14 14l3.5 3.5" /></svg>
);

// ContactType-IDs (Person/Firma) einmal auflösen + cachen.
let typeCache: { person?: number; firma?: number } | null = null;
async function resolveTypeIds(): Promise<{ person?: number; firma?: number }> {
  if (typeCache) return typeCache;
  try {
    const res = await fetch(apiUrl("contacttype"));
    const list = await res.json();
    const find = (n: string) =>
      Array.isArray(list) ? list.find((t: any) => String(t?.type).toLowerCase() === n)?.id : undefined;
    typeCache = { person: find("person"), firma: find("firma") };
  } catch {
    typeCache = {};
  }
  return typeCache;
}

function nameOf(c: any): string {
  return c?.displayName || `${c?.lastname ?? ""}${c?.firstName ? ", " + c.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim() || "—";
}

export function RelationContactPicker({
  defaultType,
  selected,
  onSelect,
  personOnly,
  excludeContactIds,
  verantwortungVonKontaktId,
  imHaushaltVon,
  nachnameVorschlag,
}: {
  defaultType: "Person" | "Firma";
  selected: any | null;
  onSelect: (c: any | null) => void;
  /** Nur Personen wählbar (Suche gefiltert, kein Person/Firma-Umschalter). */
  personOnly?: boolean;
  /** Kontakt-IDs, die nicht als Treffer erscheinen sollen (z. B. bereits zugeordnete Mitglieder). */
  excludeContactIds?: number[];
  /** T-6: Ein hier neu angelegter Kontakt erbt FO/BO dieses Kontakts (die
   *  Firma, an der die Verbindung entsteht). Weglassen = kein Erben. */
  verantwortungVonKontaktId?: number;
  /**
   * A1/D1 (Plan 2026-09-30): Der neu angelegte Kontakt lebt im Haushalt dieses
   * Kontakts — der Server übernimmt dessen Anschrift und setzt den Status
   * „Haushaltsmitglied".
   */
  imHaushaltVon?: number;
  /** D4: Nachname, der beim Neu-Anlegen einer Person vorbelegt wird (überschreibbar). */
  nachnameVorschlag?: string;
}) {
  const [mode, setMode] = React.useState<"search" | "create">("search");
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState<any[]>([]);
  const [searching, setSearching] = React.useState(false);

  const [ctype, setCtype] = React.useState<"Person" | "Firma">(defaultType);
  const [salId, setSalId] = React.useState("");
  const [sals, setSals] = React.useState<{ id: number; salutation: string }[]>([]);
  const [vorname, setVorname] = React.useState("");
  const [nachname, setNachname] = React.useState("");
  const [firmenname, setFirmenname] = React.useState("");
  const [tel, setTel] = React.useState(""); // Firma: Telefon
  const [mobile, setMobile] = React.useState(""); // Firma: Mobil
  const [email, setEmail] = React.useState(""); // Firma: E-Mail
  const [web, setWeb] = React.useState(""); // Firma: Web
  // Person: getrennt nach privat/geschäftlich.
  const [phonePrivate, setPhonePrivate] = React.useState("");
  const [phoneWork, setPhoneWork] = React.useState("");
  const [mobilePrivate, setMobilePrivate] = React.useState("");
  const [mobileWork, setMobileWork] = React.useState("");
  const [emailPrivate, setEmailPrivate] = React.useState("");
  const [emailWork, setEmailWork] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (mode !== "search") return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setSearching(true);
      fetch(apiUrl("contact", { search: term, limit: 50 }), { signal: ctrl.signal })
        .then((r) => r.json())
        .then((d) => {
          const arr = Array.isArray(d) ? d : [];
          const filtered = personOnly
            ? arr.filter((c: any) => String(c?.type?.type ?? "").toLowerCase() === "person")
            : arr;
          const notExcluded = excludeContactIds?.length
            ? filtered.filter((c: any) => !excludeContactIds.includes(c?.id))
            : filtered;
          setResults(notExcluded.slice(0, 12));
        })
        .catch(() => { /* abort/err ignorieren */ })
        .finally(() => setSearching(false));
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, mode, personOnly, excludeContactIds]);

  React.useEffect(() => {
    if (mode === "create" && ctype === "Person" && sals.length === 0) {
      fetch(apiUrl("contactsalutation"))
        .then((r) => r.json())
        .then((d) => setSals(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [mode, ctype, sals.length]);

  async function createContact() {
    setError("");
    const ids = await resolveTypeIds();
    let body: any;
    let comms: any[] = [];
    // Das Geschlecht folgt der Anrede (utils/anredeGeschlecht) und wird nicht
    // mehr eigens abgefragt. Bei einer Firma bleibt es leer.
    const abgeleitetesGeschlecht = geschlechtAusAnrede(
      sals.find((x) => String(x.id) === String(salId))?.salutation,
    );
    if (ctype === "Firma") {
      if (!firmenname.trim()) { setError("Firmenname ist erforderlich."); return; }
      // Firma: Telefon + Mobil + E-Mail (+ Web) als einzelne Kanäle.
      if (tel.trim()) comms.push({ type: "phone", value: tel.trim() });
      if (mobile.trim()) comms.push({ type: "mobile", value: mobile.trim() });
      if (email.trim()) comms.push({ type: "email", value: email.trim() });
      if (web.trim()) comms.push({ type: "homepage", value: web.trim() });
      body = { lastname: firmenname.trim(), displayName: firmenname.trim(), type: ids.firma != null ? { id: ids.firma } : { id: 1, type: "Firma" } };
    } else {
      if (!nachname.trim()) { setError("Nachname ist erforderlich."); return; }
      // Person: Telefon/Mobil/E-Mail je privat/geschäftlich → type + label.
      comms = buildPersonCommItems({ phonePrivate, phoneWork, mobilePrivate, mobileWork, emailPrivate, emailWork });
      body = {
        lastname: nachname.trim(),
        ...(vorname.trim() ? { firstName: vorname.trim() } : {}),
        ...(abgeleitetesGeschlecht ? { gender: abgeleitetesGeschlecht } : {}),
        ...(salId ? { salutation: { id: Number(salId) } } : {}),
        ...(ids.person != null ? { type: { id: ids.person } } : {}),
      };
    }
    if (comms.length) body.communications = comms;
    setSaving(true);
    try {
      const res = await fetch(apiUrl("contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mitVerantwortungVon(
            ctype === "Person" && imHaushaltVon ? { ...body, imHaushaltVon } : body,
            verantwortungVonKontaktId,
          ),
        ),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const created = await res.json();
      if (ctype === "Person" && abgeleitetesGeschlecht && !created.gender) {
        created.gender = abgeleitetesGeschlecht;
      }
      if (!created.type) created.type = { type: ctype };
      // Für den Dialog: frisch angelegt, nicht aus dem Bestand gewählt (A2
      // fragt nur beim Bestandskontakt nach der Anschrift).
      created.neuAngelegt = true;
      onSelect(created);
      setMode("search");
    } catch {
      setError("Kontakt konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  }

  /** Stand der Schnellanlage als ContactRef — für die Vollanlage. */
  const schnellanlageRef = (): ContactRef => {
    if (ctype === "Firma") {
      return { type: "Firma", manual: true, lastName: firmenname.trim(), phone: tel, mobile, email, web };
    }
    const sal = sals.find((x) => String(x.id) === String(salId));
    return {
      type: "Person",
      manual: true,
      firstName: vorname.trim(),
      lastName: nachname.trim(),
      ...(sal ? { salutationId: sal.id, salutationLabel: sal.salutation } : {}),
      phonePrivate,
      phoneWork,
      mobilePrivate,
      mobileWork,
      emailPrivate,
      emailWork,
    };
  };

  /**
   * Nach der Vollanlage: den neuen Kontakt so übergeben, wie ihn die
   * Schnellanlage aus dem POST bekäme — vollständig aus der Akte geladen.
   */
  const vollAngelegt = async (contactId: number, ref: ContactRef) => {
    let created: any = null;
    try {
      const res = await fetch(apiUrl(`contact/${contactId}`));
      if (res.ok) created = await res.json();
    } catch {
      created = null;
    }
    if (!created || typeof created !== "object") {
      created = {
        id: contactId,
        displayName: ref.displayName,
        lastname: ref.lastName,
        firstName: ref.firstName,
        gender: ref.gender,
      };
    }
    if (!created.type) created.type = { type: ref.type };
    created.neuAngelegt = true;
    onSelect(created);
    setMode("search");
  };

  if (selected) {
    return (
      <div className="rcp-selected">
        <span className="rcp-selected-name">{nameOf(selected)}</span>
        <button type="button" className="rcp-change" onClick={() => onSelect(null)}>ändern</button>
      </div>
    );
  }

  if (mode === "create") {
    return (
      <div className="rcp-create">
        {!personOnly && (
          <div className="fld">
            <label>Typ</label>
            <select className="sel" value={ctype} onChange={(e) => setCtype(e.target.value as "Person" | "Firma")}>
              <option value="Person">Privat</option>
              <option value="Firma">Firma</option>
            </select>
          </div>
        )}
        {ctype === "Person" ? (
          <>
            {/* Nur die Anrede: das Geschlecht folgt ihr (utils/anredeGeschlecht)
                und wird nicht mehr eigens abgefragt. */}
            <div className="rcp-row2">
              <div className="fld">
                <label>Anrede</label>
                <select className="sel" value={salId} onChange={(e) => setSalId(e.target.value)}>
                  <option value="">—</option>
                  {sals.map((s) => <option key={s.id} value={s.id}>{s.salutation}</option>)}
                </select>
              </div>
              <div className="fld" aria-hidden />
            </div>
            <div className="rcp-row2">
              <div className="fld"><label>Vorname</label><input className="inp" value={vorname} onChange={(e) => setVorname(e.target.value)} /></div>
              <div className="fld"><label>Nachname <span className="req">*</span></label><input className="inp" value={nachname} onChange={(e) => setNachname(e.target.value)} /></div>
            </div>
            <div className="rcp-row2">
              <div className="fld"><label>Telefon (privat)</label><TelefonEingabe className="inp" value={phonePrivate} onChange={setPhonePrivate} /></div>
              <div className="fld"><label>Telefon (geschäftlich)</label><TelefonEingabe className="inp" value={phoneWork} onChange={setPhoneWork} geschaeftlich /></div>
            </div>
            <div className="rcp-row2">
              <div className="fld"><label>Mobil (privat)</label><TelefonEingabe className="inp" value={mobilePrivate} onChange={setMobilePrivate} /></div>
              <div className="fld"><label>Mobil (geschäftlich)</label><TelefonEingabe className="inp" value={mobileWork} onChange={setMobileWork} geschaeftlich /></div>
            </div>
            <div className="rcp-row2">
              <div className="fld"><label>E-Mail (privat)</label><input className="inp" value={emailPrivate} onChange={(e) => setEmailPrivate(e.target.value)} /></div>
              <div className="fld"><label>E-Mail (geschäftlich)</label><input className="inp" value={emailWork} onChange={(e) => setEmailWork(e.target.value)} /></div>
            </div>
          </>
        ) : (
          <>
            <div className="fld"><label>Firmenname <span className="req">*</span></label><input className="inp" value={firmenname} onChange={(e) => setFirmenname(e.target.value)} /></div>
            <div className="rcp-row2">
              <div className="fld"><label>Telefon</label><TelefonEingabe className="inp" value={tel} onChange={setTel} geschaeftlich /></div>
              <div className="fld"><label>Mobil</label><TelefonEingabe className="inp" value={mobile} onChange={setMobile} geschaeftlich /></div>
            </div>
            <div className="rcp-row2">
              <div className="fld"><label>E-Mail</label><input className="inp" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div className="fld"><label>Web</label><input className="inp" value={web} onChange={(e) => setWeb(e.target.value)} placeholder="https://…" /></div>
            </div>
          </>
        )}
        {error && <div className="rcp-err">{error}</div>}
        <div className="rcp-create-actions">
          <button type="button" className="rd-pop-btn" onClick={() => setMode("search")}>Zurück zur Suche</button>
          <button type="button" className="rd-pop-btn primary" onClick={createContact} disabled={saving}>
            {saving ? "Anlegen …" : "Anlegen & übernehmen"}
          </button>
        </div>
        {/* Vollanlage (User 02.10.2026) — der Kontakt entsteht dort, hier wird
            er danach wie frisch angelegt übernommen. */}
        <VollanlageWeiterKnopf
          kontakt={schnellanlageRef}
          imHaushalt={ctype === "Person" && !!imHaushaltVon}
          eltern={{ elternId: verantwortungVonKontaktId ?? imHaushaltVon }}
          disabled={saving}
          onCreated={({ contactId, ref }) => void vollAngelegt(contactId, ref)}
        />
      </div>
    );
  }

  return (
    <div className="rcp">
      <div className="rcp-search">
        {SearchIcon}
        {/* Gesucht wird nach Kontakttyp — „Person" heißt in der Oberfläche
            „Privat(kontakt)" (utils/contactTypeLabel.ts). */}
        <input className="rcp-input" placeholder={personOnly ? "Privatkontakt suchen …" : "Privatkontakt oder Firma suchen …"} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      </div>
      {searching && <div className="rcp-msg">Suche …</div>}
      {!searching && q.trim().length >= 2 && results.length === 0 && <div className="rcp-msg">Keine Treffer.</div>}
      {results.length > 0 && (
        <div className="rcp-results">
          {results.map((c) => (
            <button type="button" key={c.id} className="rcp-result" onClick={() => onSelect(c)}>
              <span className="rcp-result-name">{nameOf(c)}</span>
              <span className="rcp-result-typ">{contactTypeLabel(c.type?.type) || ""}</span>
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        className="rcp-newlink"
        onClick={() => {
          setCtype(defaultType);
          // D4: Kind oder Partner im Haushalt tragen meist den Nachnamen des
          // Hauptkontakts — vorbelegt, aber frei überschreibbar.
          if (!nachname && nachnameVorschlag?.trim()) setNachname(nachnameVorschlag.trim());
          setMode("create");
        }}
      >
        + Neuen Kontakt anlegen
      </button>
    </div>
  );
}
